import React, { useCallback, useEffect, useState } from 'react';
import './MailPage.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// The practice's mail threads (docs/slack-pane.md, "Mail (step 2)"): the same mail_threads
// rows the Slack app's Mail tab renders, read through the session routes in
// routes/client_mail.py. Staff only; the page is not on the client view's list.
//
// A thread is the practice's correspondence: sender, subject, date, a capped preview and
// the replies people queued from Slack. No body is stored, nothing here sends mail; the
// only write is the open / answered mark.

const FILTERS = [
  { id: 'open', label: 'Open' },
  { id: 'all', label: 'All' },
];

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

async function readError(response, fallback) {
  const body = await response.json().catch(() => ({}));
  return body.error || fallback;
}

function MailPage({ selectedPractice }) {
  const practiceId = selectedPractice ? selectedPractice.id : null;
  const [threads, setThreads] = useState([]);
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [expanded, setExpanded] = useState(null); // { loading, error, thread }
  const [markingId, setMarkingId] = useState(null);

  const load = useCallback(async () => {
    if (!practiceId) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/mail${filter === 'open' ? '?open=1' : ''}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error(await readError(response, 'Could not load mail'));
      const data = await response.json();
      setThreads(Array.isArray(data.threads) ? data.threads : []);
    } catch (err) {
      setError(err.message);
      setThreads([]);
    } finally {
      setLoading(false);
    }
  }, [practiceId, filter]);

  useEffect(() => {
    setExpandedId(null);
    setExpanded(null);
    load();
  }, [load]);

  // A row click opens the thread under it: the full row plus the replies queued from
  // Slack, fetched on demand. A second click closes it.
  const handleRowClick = async (thread) => {
    if (expandedId === thread.thread_id) {
      setExpandedId(null);
      setExpanded(null);
      return;
    }
    setExpandedId(thread.thread_id);
    setExpanded({ loading: true, error: '', thread: null });
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/mail/${encodeURIComponent(thread.thread_id)}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error(await readError(response, 'Could not load the thread'));
      const data = await response.json();
      setExpanded({ loading: false, error: '', thread: data });
    } catch (err) {
      setExpanded({ loading: false, error: err.message, thread: null });
    }
  };

  // Mark answered on an open thread, Reopen on an answered one: the flag only, nothing is
  // sent. A row that no longer matches the Open filter leaves the list.
  const handleMark = async (thread, event) => {
    event.stopPropagation();
    const answered = !thread.answered;
    setMarkingId(thread.thread_id);
    setMessage(null);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/mail/${encodeURIComponent(thread.thread_id)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ answered }),
        }
      );
      if (!response.ok) throw new Error(await readError(response, 'Could not update the thread'));
      const updated = await response.json();
      if (filter === 'all') {
        setThreads((rows) => rows.map((row) => (row.thread_id === updated.thread_id ? { ...row, answered: updated.answered } : row)));
      } else {
        setThreads((rows) => rows.filter((row) => row.thread_id !== updated.thread_id));
        if (expandedId === updated.thread_id) {
          setExpandedId(null);
          setExpanded(null);
        }
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setMarkingId(null);
    }
  };

  if (!selectedPractice) {
    return (
      <div className="mail-page">
        <p className="mail-muted">Select a practice to see its mail.</p>
      </div>
    );
  }

  const renderDetail = () => {
    if (!expanded || expanded.loading) return <p className="mail-muted">Loading thread…</p>;
    if (expanded.error) return <div className="mail-message error"><span>{expanded.error}</span></div>;
    const t = expanded.thread;
    return (
      <div className="mail-detail">
        <div className="mail-detail-head">
          <div><strong>{t.subject || '(no subject)'}</strong></div>
          <div className="mail-muted">
            {t.from}{t.to ? ` → ${t.to}` : ''} · {formatWhen(t.date)} · {t.messages} message{t.messages === 1 ? '' : 's'}
            {t.last_from ? ` · last from ${t.last_from}` : ''}
          </div>
        </div>
        {t.preview && <p className="mail-detail-preview">{t.preview}</p>}
        {Array.isArray(t.replies) && t.replies.length > 0 ? (
          <ul className="mail-replies">
            {t.replies.map((r) => (
              <li key={r.id} className="mail-reply">
                <div className="mail-muted">
                  Reply from {r.from_mailbox}{r.by_name ? ` by ${r.by_name}` : ''} · {formatWhen(r.created_at)} ·{' '}
                  <span className={`status-badge status-${r.status}`}>{r.status}</span>
                </div>
                <div className="mail-reply-body">{r.body}</div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mail-muted">No replies queued.</p>
        )}
      </div>
    );
  };

  return (
    <div className="mail-page">
      <div className="mail-header">
        <div>
          <h1 className="page-title">Mail</h1>
          <p className="mail-muted">
            {selectedPractice.name} — email threads with the practice's office. Replies are queued from Slack; nothing here sends.
          </p>
        </div>
        <div className="mail-filter" role="group" aria-label="Mail filter">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`mail-filter-btn ${filter === f.id ? 'active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {message && (
        <div className={`mail-message ${message.type}`}>
          <span>{message.text}</span>
          <button type="button" className="message-close" onClick={() => setMessage(null)} aria-label="Dismiss">
            &times;
          </button>
        </div>
      )}

      {error && (
        <div className="mail-message error">
          <span>{error}</span>
          <button type="button" className="mail-retry" onClick={load}>Retry</button>
        </div>
      )}

      {loading ? (
        <p className="mail-muted">Loading mail…</p>
      ) : !error && threads.length === 0 ? (
        <p className="mail-empty">{filter === 'open' ? 'No open threads.' : 'No mail yet.'}</p>
      ) : (
        <div className="mail-table-wrap">
          <table className="mail-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>From</th>
                <th>Date</th>
                <th>Preview</th>
                <th>Status</th>
                <th>Replies</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {threads.map((thread) => (
                <React.Fragment key={thread.thread_id}>
                  <tr
                    className={`mail-row ${thread.answered ? 'answered' : ''} ${expandedId === thread.thread_id ? 'expanded' : ''}`}
                    onClick={() => handleRowClick(thread)}
                  >
                    <td className="mail-subject">{thread.subject || <span className="mail-muted">(no subject)</span>}</td>
                    <td className="mail-from">{thread.from}</td>
                    <td className="mail-when">{formatWhen(thread.date)}</td>
                    <td className="mail-preview">{thread.preview || <span className="mail-muted">—</span>}</td>
                    <td>
                      <span className={`status-badge ${thread.answered ? 'status-answered' : 'status-open'}`}>
                        {thread.answered ? 'answered' : 'open'}
                      </span>
                    </td>
                    <td className="mail-count">{thread.replies || 0}</td>
                    <td className="mail-actions">
                      <button
                        type="button"
                        className={`mail-mark ${thread.answered ? 'reopen' : 'answer'}`}
                        onClick={(e) => handleMark(thread, e)}
                        disabled={markingId === thread.thread_id}
                      >
                        {thread.answered ? 'Reopen' : 'Mark answered'}
                      </button>
                    </td>
                  </tr>
                  {expandedId === thread.thread_id && (
                    <tr className="mail-detail-row">
                      <td colSpan={7}>{renderDetail()}</td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default MailPage;
